import Form, { useForm } from 'antd/lib/form/Form'
import FormlessModal from '../../../../../../../../../../../components/CIMSModal/FormlessModal'
import { useEJLogContext } from '../../../../EJLogContext'
import CommonEJFormItems from '../CommonEJFormItems'
import { useEffect } from 'react'
import { useEJReportsSelectOptionsContext } from '../../../../../EJReportsSelectOptionsContext'
import { toMoment } from '../../../../../../../../../../../utils/toMoment'
import { usePayItemSelectContext } from '../../../../../../../../../../../components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import { useProjectCustomFieldsContext } from '../../../../../../../../../../../components/CustomFields/ProjectCustomFieldsContext'
import { EditEJModalValues } from './types'
import CreateEditEJFormItems from '../CreateEditEJFormItems'
import { errorHandler } from 'hooks/errorHandler'

const EditEJModal: React.FC = () => {
  const {
    refetch,
    edit,
    editLoading,
    editModal: { state, toggle, visible },
    metadata: { loading: metaFetching, ejTypes, ejQuickLinks },
    typeOptions,
  } = useEJLogContext()
  const {
    toLabeledValues: toLabeledReports,
    fromLabeledValues: fromLabeledReports,
  } = useEJReportsSelectOptionsContext()
  const {
    toLabeledValues: toLabeledPayItems,
    fromLabeledValues: fromLabeledPayItems,
  } = usePayItemSelectContext()
  const { toMutateCustomFieldsInput, toCustomFieldEditorValue } =
    useProjectCustomFieldsContext()
  const [form] = useForm<EditEJModalValues>()

  useEffect(() => {
    form.setFieldsValue({
      typeId: state?.typeId,
      failureDate: toMoment(state?.failureDate),
      failureDescription: state?.failureDescription,
      justification: state?.justification,
      notes: state?.notes,
      manualReportId: state?.manualReportId,
      reports: toLabeledReports(
        state?.ejToReports?.map(
          ({
            concreteTest,
            concretePlacement,
            lot,
            dailyInspectionControlPoint,
            dailyInspectionInspection,
          }) =>
            concreteTest ??
            concretePlacement ??
            lot ??
            dailyInspectionControlPoint ??
            dailyInspectionInspection!
        )
      ),
      payItems: toLabeledPayItems(
        state?.ejToPayItemNoGeometries?.map(({ payItem }) => payItem)
      ),
      externalLinks: state?.externalLinks,
      customFields: toCustomFieldEditorValue(state?.customFields),
    })
  }, [
    form,
    state,
    toCustomFieldEditorValue,
    toLabeledPayItems,
    toLabeledReports,
  ])

  return (
    <FormlessModal
      title={'Edit Engineering Judgement'}
      visible={visible}
      ok={{ fn: () => form.submit(), loading: editLoading }}
      cancel={{ fn: () => toggle(null) }}
    >
      <Form
        form={form}
        initialValues={{
          typeId: state?.typeId,
          failureDate: toMoment(state?.failureDate),
          failureDescription: state?.failureDescription,
          justification: state?.justification,
          notes: state?.notes,
          manualReportId: state?.manualReportId,
          reports: toLabeledReports(
            state?.ejToReports?.map(
              ({
                concreteTest,
                concretePlacement,
                lot,
                dailyInspectionControlPoint,
                dailyInspectionInspection,
              }) =>
                concreteTest ??
                concretePlacement ??
                lot ??
                dailyInspectionControlPoint ??
                dailyInspectionInspection!
            )
          ),
          payItems: toLabeledPayItems(
            state?.ejToPayItemNoGeometries?.map(({ payItem }) => payItem)
          ),
          externalLinks: state?.externalLinks,
          customFields: toCustomFieldEditorValue(state?.customFields),
        }}
        onFinish={async ({
          failureDate,
          reports,
          payItems,
          customFields,
          externalLinks,
          ...rest
        }) => {
          if (!state?.id)
            return errorHandler('Engineering judgement id must be defined')

          await edit({
            variables: {
              input: {
                id: state.id,
                failureDate: failureDate?.toISOString(),
                reports: fromLabeledReports(reports),
                payItemIds: fromLabeledPayItems(payItems),
                customFields: toMutateCustomFieldsInput(customFields),
                externalLinks: externalLinks?.map(({ href, label }) => ({
                  href,
                  label,
                })),
                ...rest,
              },
            },
          })
          refetch()
          toggle(null)
        }}
      >
        <CommonEJFormItems
          editing
          form={form}
          types={ejTypes}
          typeOptions={typeOptions}
          {...state}
        />
        <CreateEditEJFormItems
          loading={metaFetching}
          quickLinks={ejQuickLinks}
          {...state}
        />
      </Form>
    </FormlessModal>
  )
}

export default EditEJModal
